import { DEFAULT_USER_ID } from "../constants/user";
import type { AppCoordinator } from "../core/AppCoordinator";
import type { CommenterDefinition, Session } from "../types/core";
import { DEFAULT_COMMENTER_DEFINITIONS } from "../types/core";

export interface SettingsCallbacks {
	onCommenterDefinitionsUpdate: (definitions: CommenterDefinition[]) => void;
	onLowConfidenceThresholdUpdate: (threshold: number) => void;
}

export class SettingsDataService {
	constructor(
		private coordinator: AppCoordinator,
		private callbacks: SettingsCallbacks,
	) {}

	getAudioConfig() {
		return this.coordinator.audioAnalyzer.getConfig();
	}

	async updateAudioConfig(
		config: Partial<import("../types/core").AudioAnalysisConfig>,
	): Promise<void> {
		this.coordinator.audioAnalyzer.updateConfig(config);
		await this.coordinator.preferenceManager.updatePreferences(
			DEFAULT_USER_ID,
			{
				audioConfig: config,
			},
		);
	}

	async updateApiKeys(keys: { gemini?: string }): Promise<void> {
		const apiKeys: { summaryApiKey?: string } = {};
		if (keys.gemini !== undefined) apiKeys.summaryApiKey = keys.gemini;
		await this.coordinator.preferenceManager.updatePreferences(
			DEFAULT_USER_ID,
			{
				apiKeys,
			},
		);
	}

	async updateCommentMaxCount(count: number): Promise<void> {
		await this.coordinator.preferenceManager.updatePreferences(
			DEFAULT_USER_ID,
			{
				commentMaxCount: count,
			},
		);
	}

	async updateNotificationsEnabled(enabled: boolean): Promise<void> {
		await this.coordinator.preferenceManager.updatePreferences(
			DEFAULT_USER_ID,
			{
				notificationsEnabled: enabled,
			},
		);
	}

	async updateAutoCommentConfig(
		config: Partial<import("../types/core").AutoCommentConfig>,
	): Promise<void> {
		const currentPrefs = await this.getPreferences();
		const mergedConfig = { ...currentPrefs.autoCommentConfig, ...config };

		await this.coordinator.preferenceManager.updatePreferences(
			DEFAULT_USER_ID,
			{
				autoCommentConfig: mergedConfig,
			},
		);
		// 実行中のトリガーにも即座に反映する
		this.coordinator.autoCommentTrigger.updateConfig(mergedConfig);
	}

	async getPreferences() {
		return await this.coordinator.preferenceManager.initializePreferences(
			DEFAULT_USER_ID,
		);
	}

	async updateTranscriptionConfig(threshold: number): Promise<void> {
		await this.coordinator.preferenceManager.updatePreferences(
			DEFAULT_USER_ID,
			{
				lowConfidenceThreshold: threshold,
			},
		);
		this.callbacks.onLowConfidenceThresholdUpdate(threshold);
	}

	async updatePolicyConfig(policy: string): Promise<void> {
		await this.coordinator.preferenceManager.updatePreferences(
			DEFAULT_USER_ID,
			{
				policyConfig: { basicPolicy: policy },
			},
		);
	}

	async updateCommenterDefinitions(
		definitions: CommenterDefinition[],
	): Promise<void> {
		await this.coordinator.preferenceManager.updatePreferences(
			DEFAULT_USER_ID,
			{
				commenterDefinitions: definitions,
			},
		);
		this.callbacks.onCommenterDefinitionsUpdate(definitions);
	}

	async getPresets() {
		return await this.coordinator.presetManager.getAllPresets();
	}

	async applyPreset(id: string): Promise<void> {
		const preset = await this.coordinator.presetManager.getPreset(id);
		if (!preset) return;

		await this.coordinator.preferenceManager.applyUserPreset(
			DEFAULT_USER_ID,
			preset,
		);
		const prefs =
			await this.coordinator.preferenceManager.initializePreferences(
				DEFAULT_USER_ID,
			);
		this.callbacks.onCommenterDefinitionsUpdate(
			prefs.commenterDefinitions || DEFAULT_COMMENTER_DEFINITIONS,
		);
		this.callbacks.onLowConfidenceThresholdUpdate(
			prefs.lowConfidenceThreshold ?? 0.9,
		);
	}

	async saveCurrentAsPreset(name: string, description: string): Promise<void> {
		await this.coordinator.preferenceManager.saveCurrentAsPreset(
			DEFAULT_USER_ID,
			this.coordinator.presetManager,
			name,
			description,
		);
	}

	async deletePreset(id: string): Promise<void> {
		await this.coordinator.preferenceManager.deletePreset(
			this.coordinator.presetManager,
			id,
		);
	}

	async updatePreset(
		id: string,
		updates: { name?: string; description?: string },
	): Promise<void> {
		await this.coordinator.preferenceManager.updatePreset(
			this.coordinator.presetManager,
			id,
			updates,
		);
	}

	async exportData(): Promise<string> {
		const migration = new (
			await import("../storage/data-migration")
		).DataMigration(this.coordinator.storage);
		return JSON.stringify(await migration.exportData());
	}

	async importData(json: string): Promise<void> {
		const migration = new (
			await import("../storage/data-migration")
		).DataMigration(this.coordinator.storage);
		await migration.importData(JSON.parse(json));
	}

	async getHistory(): Promise<Session[]> {
		return await this.coordinator.sessionManager.getUserSessions(
			DEFAULT_USER_ID,
		);
	}

	async deleteSession(sessionId: string): Promise<void> {
		await this.coordinator.storage.deleteSession(sessionId);
	}

	async deleteSessions(sessionIds: string[]): Promise<void> {
		await this.coordinator.storage.deleteSessions(sessionIds);
	}

	async getAudioFiles(sessionId: string) {
		return await this.coordinator.audioManager.getAudioFilesBySession(
			sessionId,
		);
	}

	async getSummary(sessionId: string) {
		return await this.coordinator.storage.getSummary(sessionId);
	}

	async renameSession(sessionId: string, newTitle: string): Promise<void> {
		await this.coordinator.storage.updateSessionMetadata(sessionId, {
			title: newTitle,
		});
	}

	async toggleFavoriteSession(
		sessionId: string,
		isFavorite: boolean,
	): Promise<void> {
		await this.coordinator.storage.updateSessionMetadata(sessionId, {
			isFavorite,
		});
	}
}
